import "./consentGate.css";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { contractsPdf } from "../../assets/pdf";
import ContractsModal from "../Modals/ContractsModal/ContractsModal";
import {
  buildAcceptedConsentPayload,
  buildRejectedConsentRecord,
  flushPendingConsentRecords,
  getConsentVersion,
  getStoredConsent,
  hasAcceptedConsent,
  saveConsentRecord,
  submitConsentRecord,
} from "../../utils/consent";

function ConsentGate() {
  const { t, i18n } = useTranslation();
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [approvalChecked, setApprovalChecked] = useState(false);
  const [openKvk, setOpenKvk] = useState(false);
  const [openPrivacy, setOpenPrivacy] = useState(false);

  useEffect(() => {
    const storedConsent = getStoredConsent();
    if (hasAcceptedConsent(storedConsent)) {
      setAccepted(true);
    } else if (
      storedConsent?.status === "rejected" &&
      storedConsent?.version === getConsentVersion()
    ) {
      setRejected(true);
    }

    setReady(true);
  }, []);

  useEffect(() => {
    flushPendingConsentRecords();
  }, []);

  const isVisible = ready && !accepted;

  const consentItems = useMemo(
    () => [
      {
        key: "kvk",
        title: t("kvk_policy_title"),
        actionLabel: t("consent_preview"),
        open: () => setOpenKvk(true),
      },
      {
        key: "privacy",
        title: t("privacy_policy_title"),
        actionLabel: t("consent_preview"),
        open: () => setOpenPrivacy(true),
      },
    ],
    [t]
  );

  async function handleAccept() {
    if (!approvalChecked || busy) {
      return;
    }

    try {
      setBusy(true);
      const payload = await buildAcceptedConsentPayload({
        locale: i18n.language,
      });
      saveConsentRecord(payload);
      setAccepted(true);
      await submitConsentRecord(payload);
    } finally {
      setBusy(false);
    }
  }

  function handleReject() {
    const rejectedRecord = buildRejectedConsentRecord();
    saveConsentRecord(rejectedRecord);
    setRejected(true);
  }

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div className="consent-gate-overlay" role="dialog" aria-modal="true">
        <div className="consent-gate-card">
          <div className="consent-gate-header">
            <span className="consent-pill">{t("consent_pill")}</span>
            <h2>{t("consent_title")}</h2>
            <p>{t("consent_description")}</p>
          </div>

          <div className="consent-documents">
            {consentItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className="consent-document-button"
                onClick={item.open}
              >
                <span className="material-symbols-outlined">description</span>
                <span>{item.title}</span>
                <span className="consent-document-action">{item.actionLabel}</span>
              </button>
            ))}
          </div>

          {rejected ? (
            <div className="consent-rejected-state">
              <p>{t("consent_rejected_message")}</p>
              <button
                type="button"
                className="consent-secondary"
                onClick={() => setRejected(false)}
              >
                {t("consent_review_again")}
              </button>
            </div>
          ) : (
            <>
              <label className="consent-checkbox-row">
                <input
                  type="checkbox"
                  checked={approvalChecked}
                  onChange={(event) => setApprovalChecked(event.target.checked)}
                />
                <span>{t("consent_checkbox_label")}</span>
              </label>

              <div className="consent-actions">
                <button
                  type="button"
                  className="consent-secondary"
                  onClick={handleReject}
                  disabled={busy}
                >
                  {t("consent_reject")}
                </button>
                <button
                  type="button"
                  className="consent-primary"
                  onClick={handleAccept}
                  disabled={!approvalChecked || busy}
                >
                  {busy ? t("loading") : t("consent_accept")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {openKvk ? (
        <ContractsModal
          contractTitle={t("kvk_policy_title")}
          contractText={contractsPdf.kvkPolicy}
          handleCloseModal={() => setOpenKvk(false)}
        />
      ) : null}

      {openPrivacy ? (
        <ContractsModal
          contractTitle={t("privacy_policy_title")}
          contractText={contractsPdf.cookiePolicy}
          handleCloseModal={() => setOpenPrivacy(false)}
        />
      ) : null}
    </>
  );
}

export default ConsentGate;
